import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareModal from '@/components/base/ShareModal';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { plotEpisodes } from '@/mocks/plotEpisodes';
import { episodeNo, formatDateCn } from '@/utils/format';

export default function PlotDetail() {
  const { id } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const episode = plotEpisodes.find((item) => String(item.id) === id);

  usePageMeta({
    title: episode
      ? `禁止的爱善良的小姨子 — 分集${episode.title}剧情介绍`
      : '剧情详情 - 禁止的爱善良的小姨子专题',
    description: episode
      ? `${episodeNo(episode.episode)}《${episode.title}》剧情介绍：${episode.summary}包含播出日期、时长与完整剧情梗概。`
      : '禁止的爱善良的小姨子分集剧情详情页。',
    keywords: episode
      ? `${episode.title},禁止的爱善良的小姨子分集剧情,第${episode.episode}集剧情`
      : '禁止的爱善良的小姨子,分集剧情',
  });

  if (!episode) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-8">
          <EmptyState
            title="没有找到这一集"
            description="该分集可能尚未收录，返回剧情分类页查看全部分集。"
            actionLabel="返回剧情列表"
            actionTo="/category/plot"
          />
        </div>
      </SiteLayout>
    );
  }

  const related = plotEpisodes.filter((item) => item.id !== episode.id).slice(0, 3);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-5 md:px-8">
          <Breadcrumb
            items={[
              { label: '剧情', to: '/category/plot' },
              { label: episodeNo(episode.episode) },
            ]}
          />
        </div>
      </div>

      <article className="mx-auto w-full max-w-[1120px] px-4 pb-24 pt-8 md:px-8 md:pt-10 lg:pb-10">
        <header>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-sm bg-primary-600 px-2 py-1 text-background-50">{episodeNo(episode.episode)}</span>
            <span className="text-foreground-400">分集编号 {String(episode.episode).padStart(2, '0')}</span>
            <span className="h-3 w-[1px] bg-background-300" />
            <span className="text-foreground-400">{episode.duration}</span>
          </div>
          <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-950 md:text-4xl">{episode.title}</h1>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-500">播出日期 · {formatDateCn(episode.date)}</p>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:flex"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-share-line" />
              </span>
              分享本集
            </button>
          </div>
        </header>

        <div className="mt-6 h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
          <img
            src={episode.image}
            alt={`${episodeNo(episode.episode)} ${episode.title} 剧照`}
            title={`${episode.title} - 禁止的爱善良的小姨子剧情`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
              <span className="h-4 w-[3px] rounded-full bg-primary-600" />
              详细剧情
            </h2>
            <div className="mt-4 space-y-4 text-[15px] leading-[1.95] text-foreground-700">
              {episode.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 12)}>{paragraph}</p>
              ))}
            </div>

            <div className="mt-6 rounded-lg border-l-2 border-accent-500 bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
              本页为剧情资料整理，不含视频播放与下载内容。剧照与人物信息均为专题演示素材。
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h2 className="font-heading text-base text-foreground-900">分集信息</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">分集编号</dt>
                  <dd className="text-foreground-800">{episodeNo(episode.episode)}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">播出日期</dt>
                  <dd className="text-foreground-800">{episode.date}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">单集时长</dt>
                  <dd className="text-foreground-800">{episode.duration}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-400">所属剧集</dt>
                  <dd className="text-foreground-800">禁止的爱善良的小姨子</dd>
                </div>
              </dl>
              <Link
                to="/category/plot"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-700"
              >
                返回剧情列表
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
              <p className="mt-3 text-center text-[11px] text-foreground-400">
                可以在剧情页切换升序 / 降序查看全部集数
              </p>
            </div>
          </aside>
        </div>

        <section className="mt-10 border-t border-background-200 pt-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-heading text-lg text-foreground-900">相关剧情推荐</h2>
            <Link
              to="/category/plot"
              className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              查看全部分集
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/detail/plot/${item.id}`}
                className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-[130px] w-full overflow-hidden bg-background-200">
                  <img
                    src={item.image}
                    alt={`${episodeNo(item.episode)} ${item.title} 剧照`}
                    title={`${item.title} - 禁止的爱善良的小姨子剧情`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-4">
                  <p className="text-[11px] text-primary-600">{episodeNo(item.episode)}</p>
                  <h3 className="mt-1 font-heading text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                    {item.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm text-background-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-line" />
          </span>
          分享本集
        </button>
        <Link
          to="/category/plot"
          className="flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700"
        >
          全部剧情
        </Link>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${episodeNo(episode.episode)} ${episode.title}`} />
    </SiteLayout>
  );
}